// Components/MatchSchedule.jsx
import React, { useEffect, useState, useRef } from 'react';
import './MatchesSchedule.css';

const API = process.env.REACT_APP_API;

function MatchSchedule({ filterLeague = null, filterStage = null }) {
  const [matches, setMatches] = useState([]);
  const [leagueName, setLeagueName] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false); // ✅ כדי להימנע מהצגת טקסטים לפני הזמן
  const [isFetching, setIsFetching] = useState(false);

  const tableWrapperRef = useRef(null); // ✅

  function formatDate(iso) {
    if (!iso)
      return "";
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  
  useEffect(() => {
    let cancelled = false;
    const fetchMatches = async () => {
      setIsFetching(true);
      setLoading(true);

      try {
        if (!filterLeague) {
          const res = await fetch(`${API}/api/matches/upcoming`);
          const data = await res.json();
          if (cancelled) return;
          setMatches(Array.isArray(data) ? data : []);
          setLeagueName("");
          setInitialized(true);
        } else if (filterLeague && !filterStage) {
          // אם נבחרה ליגה אבל עוד לא נבחר שלב
          if (cancelled) return;
          setMatches([]);
          setLeagueName(filterLeague?.name || "");
          setInitialized(true);
        } else {
          // יש ליגה ושלב
          const url = `${API}/api/matches/${filterLeague._id}/stage?stageId=${filterStage._id}`;
          const res = await fetch(url);
          const data = await res.json();
          if (cancelled) return;
          setMatches(Array.isArray(data.matches) ? data.matches : []);
          setLeagueName(data.leagueName || filterLeague.name || "");
          setInitialized(true);
        }
      } catch (err) {
        console.error("❌ שגיאה בשליפת המשחקים מהשרת:", err);
        if (!cancelled) {
          setMatches([]);
          setInitialized(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setTimeout(() => setIsFetching(false), 150); // השהייה קטנה למניעת הבזקי מעבר
        }
      }
    };

    fetchMatches();
    return () => { cancelled = true; };
  }, [filterLeague, filterStage]);

  // ✅ גלילה אוטומטית למשחק הקרוב להיום
  useEffect(() => {
    if (!matches.length || !tableWrapperRef.current || !filterLeague) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // השווה רק תאריכים בלי שעה

    const rows = tableWrapperRef.current.querySelectorAll('tbody tr');
    let closestRow = null;
    let minDiff = Infinity;

    rows.forEach(row => {
      const parts = row.querySelector('.date-column')?.textContent.trim().split('/') || [];
      if (parts.length < 3) return;

      const matchDate = new Date(+parts[2], +parts[1] - 1, +parts[0]);
      const diff = matchDate - today;

      if (diff >= 0 && diff < minDiff) {
        minDiff = diff;
        closestRow = row;
      }

      // הסר הדגשה קודמת
      row.classList.remove('closest-match');
    });

    if (closestRow) {
      closestRow.classList.add('closest-match');
      const headerOffset = 80; // התאמה לפי כותרת
      const topPos = closestRow.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: topPos, behavior: 'smooth' });
    }
  }, [matches, filterLeague]);


  // 🌀 בזמן טעינה — לא מציגים שום דבר חוץ מספינר
  if (loading || isFetching || !initialized) {
    return (
      <div className="loading-spinner-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // 🏐 אין משחקים
  if (initialized && (!matches || matches.length === 0)) {
    if (filterLeague && !filterStage) {
      return <p className="choose-a-stage">נא לבחור שלב</p>;
    }

    return (
      <p className="no-matches-message">
        {`אין משחקים בשלב שנבחר${filterLeague ? ` בליגת "${leagueName}"` : ''} להצגה`}
      </p>
    );
  }

  return (
    <div className="match-schedule-container">
      <div className="match-table-wrapper" ref={tableWrapperRef}>
        <table className="match-table">
          <thead>
            <tr>
              <th colSpan="6" className="last-update">
                {matches?.length > 0 && (
                  <>
                    עדכון אחרון: {new Date(matches[0].lastUpdated).toLocaleDateString("he-IL")}
                  </>
                )}
              </th>
            </tr>
            <tr>
              {!filterLeague && <th className='league-column'>ליגה</th>}
              <th>תאריך / שעה</th>
              <th>הקבוצות</th>
              {filterLeague && (
                <>
                  <th className='result-column'>תוצאה</th>
                  <th>מע' 1</th>
                  <th>מע' 2</th>
                  <th>מע' 3</th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {matches.map((match, i) => (
              <tr key={match._id || i}>
                {!filterLeague && (
                  <td className="league-column">{match.leagueInfo?.name || match.league}</td>
                )}
                <td className="date-column">
                  {formatDate(match.date)} <br /> {match.time} - {match.venue}
                </td>
                <td className="teams-column">
                  <div className="teams-wrapper">
                    <div className="teamA">{match.teamA}</div>
                    <div className="teamB">{match.teamB}</div>
                  </div>
                  </td>
                {filterLeague && (
                  <>
                    <td className="result-column">{match.setsA ?? ""} - {match.setsB ?? ""}</td>
                    <td>{match.sets?.find(s => s.setNumber === 1)?.scoreA ?? ""} - {match.sets?.find(s => s.setNumber === 1)?.scoreB ?? ""}</td>
                    <td>{match.sets?.find(s => s.setNumber === 2)?.scoreA ?? ""} - {match.sets?.find(s => s.setNumber === 2)?.scoreB ?? ""}</td>
                    <td>{match.sets?.find(s => s.setNumber === 3)?.scoreA ?? ""} - {match.sets?.find(s => s.setNumber === 3)?.scoreB ?? ""}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
};

export default MatchSchedule;
